import { CommonModule, NgIf } from '@angular/common'
import { Component, Input, OnChanges } from '@angular/core'
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { ButtonComponent } from '@progress/kendo-angular-buttons'
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns'
import { AddCommandDirective, CancelCommandDirective, CellTemplateDirective, ColumnComponent, CommandColumnComponent, DataBindingDirective, EditCommandDirective, EditTemplateDirective, FocusableDirective, GridComponent, GridToolbarFocusableDirective, RemoveCommandDirective, SaveCommandDirective, ToolbarTemplateDirective } from '@progress/kendo-angular-grid'
import { ClientServices } from '../../../../@theme/services/clients.service'
import { TypesServices } from '../../../../@theme/services/typeslist.service'
import { RepPro } from '../../../../models/repEditorModels'
import { GridEditorBaseComponent } from '../../shared/rep-editor-base/grid-editor-base-component'
import { RepEditorStateService } from '../../shared/rep-editor-state.service'
import { AdminOnlyDirective } from '../shared/admin-only.directive'

@Component({
    selector: 'gd-rep-editor-professional',
    templateUrl: './rep-editor-professional.component.html',
    styleUrls: ['./rep-editor-professional.component.scss'],
    imports: [
        CommonModule,
        GridComponent,
        DataBindingDirective,
        ToolbarTemplateDirective,
        GridToolbarFocusableDirective,
        AddCommandDirective,
        ButtonComponent,
        ColumnComponent,
        EditTemplateDirective,
        DropDownListComponent,
        FormsModule,
        ReactiveFormsModule,
        CommandColumnComponent,
        CellTemplateDirective,
        FocusableDirective,
        EditCommandDirective,
        NgIf,
        RemoveCommandDirective,
        SaveCommandDirective,
        CancelCommandDirective,
        AdminOnlyDirective
    ]
})
export class RepEditorProfessionalComponent
  extends GridEditorBaseComponent
  implements OnChanges
{
  @Input() override repId: number = 0
  repPro: RepPro[]
  proTypes: any[]
  private activeProTypesSource: any[]
  private activeProTypesCache: any[] = []

  // Cached so the dropdown gets a stable array reference; a new array on every
  // change detection resets its scroll position.
  get activeProTypes() {
    if (this.repState.proTypes !== this.activeProTypesSource) {
      this.activeProTypesSource = this.repState.proTypes
      this.activeProTypesCache = this.repState.proTypes
        ? [...this.repState.getActiveProTypes()].sort((a, b) =>
            (a.proTypeDescription ?? '').localeCompare(b.proTypeDescription ?? '')
          )
        : []
    }
    return this.activeProTypesCache
  }

  constructor(
    typesService: TypesServices,
    clientService: ClientServices,
    formBuilder: UntypedFormBuilder,
    repEditorStateService: RepEditorStateService,
  ) {
    super(typesService, clientService, formBuilder, repEditorStateService)
  }

  ngOnChanges() {
    this.checkGridState()

    if (!this.repState.repPros) {
      this.initGrid()
    }

    if (!this.repState.proTypes) {
      this.typesService.getProTypes().subscribe((response) => {
        this.repState.proTypes = response
      })
    }
  }

  initGrid() {
    this.clientService
      .getRepProForRep({ repId: this.repId })
      .subscribe((response) => {
        this.repState.repPros = response.map((rp) => new RepPro(rp))
      })
  }

  public addHandler({ sender }) {
    const newRepPro = new RepPro(null)
    newRepPro.repId = this.repId
    newRepPro.active = true
    this.addHandlerShared({ sender }, newRepPro)
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew }): void {
    const repPro = new RepPro(formGroup.value)
    this.clientService.submitRepProUpdate(repPro).subscribe(() => {
      sender.closeRow(rowIndex)
      this.initGrid()
    })
  }

  public removeHandler({ dataItem }) {
    const itemToRemove = new RepPro(dataItem)
    itemToRemove.active = false
    this.clientService.submitRepProUpdate(itemToRemove).subscribe(() => {
      this.initGrid()
    })
  }

  public createFormGroup(repPro: RepPro): UntypedFormGroup {
    return this.formBuilder.group({
      repProId: repPro.repProId,
      repId: repPro.repId,
      proTypeId: [repPro.proTypeId, Validators.required],
      proTypeString: repPro.proTypeString,
      active: repPro.active,
    })
  }
}
